/**
 * Live metric-catalog discovery hook.
 *
 * Powers the "Discover metrics" drawer + per-row metric-name
 * autocomplete that the SLO editor surfaces for ``kind=custom``
 * PromQL rows (see ``MetricCatalogDrawer`` + ``SLOEditor``). The
 * controller scrapes one ready replica's ``/metrics`` through Envoy
 * on demand — see ``controller/controller/api/routers/metric_catalog.py``
 * for the server-side contract.
 *
 * History: this used to live in ``custom_metrics.ts`` alongside CRUD
 * wrappers for the standalone ``CustomMetric`` table. That table was
 * dropped (alembic ``0034_drop_custom_metrics``) when the WebUI's
 * Custom Metrics section was folded into the SLO loop's ``custom``
 * PromQL row, leaving only the catalog hook to keep.
 */
import { useQuery } from "@tanstack/react-query";

import { api } from "@/api/client";
import type { MetricCatalogResponse } from "@/api/types";

const CATALOG_KEY = (serviceId: number) =>
  ["services", serviceId, "metric-catalog"] as const;

/**
 * Live metric-catalog scrape via the controller.
 *
 * ``enabled=false`` by default so we don't fire a scrape every time
 * the Autoscaling tab mounts; the operator opts in by pressing the
 * "Discover metrics" button which sets ``enabled=true`` (and pressing
 * Refresh calls ``refetch()`` against the cached result).
 *
 * ``retry: false`` because the most common failure mode here — no
 * ready replicas — is a deterministic 503 the operator can fix by
 * pressing Scale Up; retrying just spams the controller until they
 * do.
 */
export function useMetricCatalog(serviceId: number, enabled: boolean) {
  return useQuery({
    queryKey: CATALOG_KEY(serviceId),
    queryFn: () =>
      api.get<MetricCatalogResponse>(
        `/services/${serviceId}/metric-catalog`,
      ),
    enabled,
    retry: false,
    staleTime: 60_000,
  });
}
